// ── Current Time ──
// Pure helpers for the current-time treatment: which trip day is "today",
// where the now-line sits, and which items have already ended. `now` is
// epoch milliseconds (see hooks/useNow). "Today" is decided per date in that
// date's own display zone, so timezone overrides and DST are respected.

import { Temporal } from "temporal-polyfill";
import type { DayBlock, DayView } from "@poi-planner/shared";
import { blockEndTime } from "./dayViews";
import { dayStart } from "./timeGrid";
import { addDaysToDate, epochMs, type ZonedTimeString } from "./zonedTime";

/** Whether `now` falls within `date`'s local day in `zone`. */
export function dayContainsNow(
  date: string,
  zone: string,
  now: number,
): boolean {
  return (
    dayStart(date, zone).epochMilliseconds <= now &&
    now < dayStart(addDaysToDate(date, 1), zone).epochMilliseconds
  );
}

/** The day view that contains `now`, or null when the trip isn't under way. */
export function findTodayView(
  dayViews: DayView[],
  now: number,
): DayView | null {
  return (
    dayViews.find((dayView) =>
      dayContainsNow(dayView.date, dayView.zone, now),
    ) ?? null
  );
}

/** `now` as a zoned string in `zone`, for the time-grid geometry helpers. */
export function nowInZone(now: number, zone: string): ZonedTimeString {
  return Temporal.Instant.fromEpochMilliseconds(now)
    .toZonedDateTimeISO(zone)
    .toString();
}

/** Local wall time ("HH:mm") of `now` in `zone`. */
export function nowTimeLabel(now: number, zone: string): string {
  const local =
    Temporal.Instant.fromEpochMilliseconds(now).toZonedDateTimeISO(zone);
  return `${String(local.hour).padStart(2, "0")}:${String(local.minute).padStart(2, "0")}`;
}

/** A timed item has ended once its end instant has passed. */
export function isTimedItemPast(
  endTime: ZonedTimeString,
  now: number,
): boolean {
  return epochMs(endTime) <= now;
}

/**
 * Whether a calendar block's item has ended. Uses the whole item's end, so
 * every day segment of a multi-day item dims together.
 */
export function isBlockPast(block: DayBlock, now: number): boolean {
  return isTimedItemPast(blockEndTime(block), now);
}

/** An all-day note has ended once its last date is over in that date's zone. */
export function isAllDayNotePast(
  endDate: string,
  zone: string,
  now: number,
): boolean {
  return now >= dayStart(addDaysToDate(endDate, 1), zone).epochMilliseconds;
}
